import MaterialReactTable from 'material-react-table'
import EditSharpIcon from '@mui/icons-material/EditSharp'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteForeverRoundedIcon from '@mui/icons-material/DeleteForeverRounded';
import ArticleTwoToneIcon from '@mui/icons-material/ArticleTwoTone';
import PrintIcon from '@mui/icons-material/Print';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from "react";
import {Button, Box} from '@mui/material';
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import ReportExportDropdown from './ReportExportDropdown';

const Status = ({ value, row, compact = false }) => {
    const navigate = useNavigate();
    const handleClick = () => {
        Swal.fire({title: 'You cannot edit delivery challans related to this work schedule further..Do you wish to confirm?',  
        showCancelButton: true,  
        confirmButtonText: `Yes`,  
        cancelButtonText: `No`,
        }).then((result) => {  
        if (result.isConfirmed) { 
            navigate(`/JobCompletion/${row.original.id}/`);
        } 
        });
    };

    const colorMap = {
        not_started: 'warning',
        in_progress: 'primary',
        to_complete: 'success',
        completed: 'error'
    };
    const buttonColor = colorMap[value];
    const transformedValue = value.includes('_') ? value.replace('_', ' ') : value;

    if (value === 'in_progress' || value === 'not_started' || value === 'completed' || value === 'to_complete') {
        return (
            <Button
                color={buttonColor}
                variant="contained"
                size="small"
                onClick={value === 'to_complete' ? handleClick : undefined}
                sx={compact ? { minHeight: 20, py: 0, px: 0.75, fontSize: 11, lineHeight: 1.2 } : undefined}
            >
                {transformedValue}
            </Button>
        );
    }

    return (
        <Box component="span"
            sx={{
                backgroundColor:
                    value.toLowerCase() === 'active' ? 'success.dark'
                        : value.toLowerCase() === 'inactive' ? 'error.dark' : 'warning.dark',
                borderRadius: '0.25rem',
                color: '#fff',
                maxWidth: compact ? 'none' : '9ch',
                p: compact ? '1px 6px' : '0.25rem',
                fontSize: compact ? 11 : undefined,
                lineHeight: compact ? 1.2 : undefined,
                textTransform: 'capitalize',
                fontWeight: 'bold',
                display: 'inline-block',
            }}
        >
            {value}
        </Box>
    );
}

function ReactTableWrapper({
    title,
    columns,
    data,
    onRowEdit,
    onRowView,
    onRowCancel,
    onRowDelete,
    onCSVExport,
    onPDFExport,
    enableExportToolbar = true,
    isLoading = true,
    hideDelete = false,
    hidePrint = true,
    onRowBrPrint,
    onRowPrint,
    left_pinned,
    right_pinned,
    autoPinActionColumn = false,
    enableTableSearch = true,
    initialSorting = [],
    layoutFullWidth = false,
    compactRows = true,
    defaultPageSize = 5,
    initialHiddenColumns = [],
}) {

    const tableInstanceRef = useRef(null);
    const [visibleRows, setVisibleRows] = useState([]);
    const [pagination, setPagination] = useState({
        pageIndex: 0,
        pageSize: defaultPageSize,
    });

    const hasCustomActionColumn = columns.some((col) => col.headerName === 'ROW_ACTIONS');

    const actionColumnField = useMemo(() => {
        const actionCol = columns.find((col) => col.headerName === 'ROW_ACTIONS');
        return actionCol?.fieldName ?? 'id';
    }, [columns]);

    const isCompactTable = layoutFullWidth || compactRows;

    const actionButtonSlotCount = useMemo(() => {
        let count = 0;
        if (onRowEdit || onRowView) {
            count += 1;
        }
        if (onRowCancel) {
            count += 1;
        }
        if (!hideDelete && onRowDelete) {
            count += 1;
        }
        if (!hidePrint && onRowPrint) {
            count += 1;
        }
        if (onRowBrPrint) {
            count += 1;
        }
        return count || 1;
    }, [hideDelete, hidePrint, onRowBrPrint, onRowCancel, onRowDelete, onRowEdit, onRowPrint, onRowView]);

    const actionColumnWidth = useMemo(() => {
        const iconSlot = isCompactTable ? 20 : 28;
        const cellPadding = isCompactTable ? 10 : 14;
        const headerMin = 56;
        return Math.max(headerMin, actionButtonSlotCount * iconSlot + cellPadding);
    }, [actionButtonSlotCount, isCompactTable]);

    const frozenRightColumnDefs = useMemo(() => {
        if (!autoPinActionColumn || !hasCustomActionColumn) {
            return [];
        }
        return columns.filter((col) => col.freezeRight || col.headerName === 'ROW_ACTIONS');
    }, [autoPinActionColumn, columns, hasCustomActionColumn]);

    const useFrozenRightPanel = frozenRightColumnDefs.length > 0;

    const resolvedRightPinned = useMemo(() => {
        if (useFrozenRightPanel) {
            return [];
        }
        const pinned = right_pinned != null ? [...right_pinned] : [];
        if (hasCustomActionColumn && !pinned.includes(actionColumnField)) {
            pinned.push(actionColumnField);
        }
        return pinned;
    }, [actionColumnField, hasCustomActionColumn, right_pinned, useFrozenRightPanel]);

    const columnPinningState = useMemo(() => ({
        left: left_pinned || [],
        right: resolvedRightPinned,
    }), [left_pinned, resolvedRightPinned]);

    const isFrozenColumn = useCallback((col) => (
        frozenRightColumnDefs.some((frozenCol) => frozenCol.fieldName === col.fieldName)
    ), [frozenRightColumnDefs]);

    const getFrozenColumnWidth = useCallback((col) => {
        if (col.headerName === 'ROW_ACTIONS') {
            return actionColumnWidth;
        }
        if (col.headerName.toLowerCase() === 'status') {
            return layoutFullWidth ? 88 : 100;
        }
        if (col.freezeRight) {
            return col.freezeWidth || (layoutFullWidth ? 100 : 120);
        }
        return col.freezeWidth || 88;
    }, [actionColumnWidth]);

    const frozenPanelWidth = useMemo(
        () => frozenRightColumnDefs.reduce((sum, col) => sum + getFrozenColumnWidth(col), 0),
        [frozenRightColumnDefs, getFrozenColumnWidth],
    );

    const syncVisibleRows = useCallback(() => {
        setVisibleRows(tableInstanceRef.current?.getRowModel()?.rows ?? []);
    }, []);

    useLayoutEffect(() => {
        syncVisibleRows();
    });

    useEffect(() => {
        syncVisibleRows();
    }, [data, isLoading, syncVisibleRows]);

    const renderActionButtons = useCallback((rowData) => {
        if (!rowData) {
            return null;
        }

        const renderedCellValue = rowData.id;
        const is_br_needed = rowData.is_batch_report_needed;
        const hide_row_edit = rowData.hide_row_edit;
        const hide_row_delete = rowData.hide_row_delete;
        const hide_row_cancel = rowData.hide_row_cancel;
        const hide_row_print = rowData.hide_row_print;
        const iconSize = isCompactTable ? '18px' : '24px';
        const iconMargin = isCompactTable ? '0px' : '3px';
        const actionButtonClass = useFrozenRightPanel ? 'mrt-frozen-action-buttons' : 'mrt-action-buttons';

        const isReadOnlyRow = hide_row_edit === 'Yes';
        const openHandler = onRowView || onRowEdit;

        return (
            <span className={actionButtonClass}>
                {isReadOnlyRow && openHandler && (
                    <VisibilityOutlinedIcon
                        titleAccess="View"
                        onClick={(event) => openHandler(renderedCellValue, event)}
                        sx={{
                            cursor: 'pointer',
                            color: 'white',
                            fontSize: iconSize,
                            backgroundColor: 'info.dark',
                            margin: iconMargin,
                            borderRadius: '3px',
                            padding: '0',
                        }}
                    />
                )}
                {!isReadOnlyRow && onRowEdit && (
                    <EditSharpIcon
                        titleAccess="Edit"
                        onClick={(event) => onRowEdit(renderedCellValue, event)}
                        sx={{
                            cursor: 'pointer',
                            color: 'white',
                            fontSize: iconSize,
                            backgroundColor: 'primary.dark',
                            margin: iconMargin,
                            borderRadius: '3px',
                            padding: '0',
                        }}
                    />
                )}
                {((onRowCancel) && (hide_row_cancel == undefined || hide_row_cancel === "No") ?
                    <CancelOutlinedIcon
                        titleAccess="Cancel voucher"
                        onClick={(event) => onRowCancel(renderedCellValue, event)}
                        sx={{
                            cursor: 'pointer',
                            color: 'white',
                            fontSize: iconSize,
                            backgroundColor: 'warning.dark',
                            margin: iconMargin,
                            borderRadius: '3px',
                            padding: '0',
                        }}
                    /> : null)}
                {((!hideDelete) && (hide_row_delete == undefined || hide_row_delete === "No") ?
                    <DeleteForeverRoundedIcon
                        onClick={(event) => onRowDelete(renderedCellValue, event)}
                        sx={{
                            cursor: 'pointer',
                            color: 'white',
                            fontSize: iconSize,
                            backgroundColor: 'error.dark',
                            margin: iconMargin,
                            borderRadius: '3px',
                            padding: '0',
                        }}
                    /> : null)}
                {((is_br_needed !== undefined) && (is_br_needed == 'Yes') && (hide_row_print === "No")) &&
                    <ArticleTwoToneIcon
                        onClick={(event) => onRowBrPrint(renderedCellValue, event)}
                        sx={{
                            cursor: 'pointer',
                            color: 'white',
                            fontSize: iconSize,
                            backgroundColor: 'warning.dark',
                            margin: iconMargin,
                            borderRadius: '3px',
                            padding: '0',
                        }}
                    />
                }
                {((!hidePrint) && (hide_row_print == undefined || hide_row_print === "No") ?
                    <PrintIcon
                        onClick={(event) => onRowPrint(renderedCellValue, event)}
                        sx={{
                            cursor: 'pointer',
                            color: 'white',
                            fontSize: iconSize,
                            backgroundColor: 'success.dark',
                            margin: iconMargin,
                            borderRadius: '3px',
                            padding: '0',
                        }}
                    /> : null)}
            </span>
        );
    }, [hideDelete, hidePrint, isCompactTable, onRowBrPrint, onRowCancel, onRowDelete, onRowEdit, onRowPrint, onRowView, useFrozenRightPanel]);

    const renderFrozenCell = useCallback((col, rowData) => {
        if (!rowData) {
            return null;
        }

        if (col.headerName === 'ROW_ACTIONS') {
            return renderActionButtons(rowData);
        }

        if (col.headerName.toLowerCase() === 'status') {
            return (
                <div style={{ textAlign: col.align || 'left', lineHeight: 1 }}>
                    <Status value={rowData[col.fieldName]} row={{ original: rowData }} compact={isCompactTable} />
                </div>
            );
        }

        const value = rowData[col.fieldName];
        return col.dataStyle ? <span style={col.dataStyle}>{value}</span> : value;
    }, [isCompactTable, renderActionButtons]);
    
    const tableColumns = useMemo(() => {
        return columns
            .filter((col) => !isFrozenColumn(col))
            .map((col) => {

            const cellAlign = col.align || 'left';
            let columnProperties = {
                accessorKey: col.fieldName,
                header: col.headerName,
                grow: layoutFullWidth,
            };

            if (col.size != null) {
                columnProperties.size = col.size;
            }
            if (col.minSize != null) {
                columnProperties.minSize = col.minSize;
            }
            if (col.maxSize != null) {
                columnProperties.maxSize = col.maxSize;
            }

            if (col.headerName === 'ROW_ACTIONS') {
                columnProperties = {
                    ...columnProperties,
                    header: 'Action',
                    Header: <span style={col.headerStyle}>Action</span>,
                    size: actionColumnWidth,
                    minSize: actionColumnWidth,
                    maxSize: actionColumnWidth + 4,
                    grow: false,
                    enableResizing: false,
                    muiTableHeadCellProps: {
                        align: 'center',
                        className: 'mrt-action-column-cell',
                        sx: { textAlign: 'center' },
                    },
                    muiTableBodyCellProps: {
                        align: 'center',
                        className: 'mrt-action-column-cell',
                        sx: {
                            textAlign: 'center',
                            overflow: 'visible',
                            px: '4px',
                        },
                    },
                    Cell: ({ renderedCellValue }) => {
                        const rowData = data.find((obj) => obj.id === renderedCellValue);
                        return renderActionButtons(rowData);
                    },
                };
            } else if (col.headerName.toLowerCase() === 'status') {

                columnProperties = {
                    ...columnProperties,
                    header: col.headerName,
                    Cell: ({renderedCellValue, row}) => (
                        <div style={{ textAlign: cellAlign, lineHeight: 1 }}>
                            <Status value={renderedCellValue} row={row} compact={isCompactTable} />
                        </div>
                    ),
                };

            } else {

                if (col.headerContent) {
                    columnProperties = {
                        ...columnProperties,
                        Header: () => col.headerContent,
                    };
                } else if (col.headerStyle) {
                    columnProperties = {
                        ...columnProperties,
                        Header: <span style={col.headerStyle}>{col.headerName}</span>,
                    };
                }

                const headAlign = col.headAlign || 'center';
                const bodyAlign = col.align === 'right' ? 'right' : (col.align === 'center' ? 'center' : 'left');
                columnProperties = {
                    ...columnProperties,
                    muiTableHeadCellProps: {
                        align: headAlign === 'right' ? 'right' : (headAlign === 'left' ? 'left' : 'center'),
                        sx: {
                            verticalAlign: 'middle',
                            textAlign: headAlign === 'right' ? 'right' : (headAlign === 'left' ? 'left' : 'center'),
                            fontWeight: 700,
                            color: '#ffffff',
                        },
                    },
                    muiTableBodyCellProps: {
                        align: bodyAlign,
                        sx: isCompactTable ? {
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        } : undefined,
                    },
                    Cell: ({ renderedCellValue, row }) => {
                        const value = renderedCellValue ?? row?.original?.[col.fieldName];
                        return (
                        <div
                            style={{
                                textAlign: cellAlign,
                                width: '100%',
                                display: 'block',
                                whiteSpace: isCompactTable ? 'nowrap' : undefined,
                                overflow: isCompactTable ? 'hidden' : undefined,
                                textOverflow: isCompactTable ? 'ellipsis' : undefined,
                            }}
                            title={isCompactTable && value != null ? String(value) : undefined}
                        >
                            {col.dataStyle ? <span style={col.dataStyle}>{value}</span> : value}
                        </div>
                        );
                    },
                };
            }

            return columnProperties;
        });
    }, [actionColumnWidth, columns, data, isCompactTable, isFrozenColumn, layoutFullWidth, renderActionButtons]);

    const wrapperClassName = [
        'react-table-wrapper',
        layoutFullWidth ? 'react-table-full-width react-table-excel-compact' : '',
        compactRows ? 'react-table-compact-rows' : '',
        useFrozenRightPanel ? 'react-table-split-freeze' : '',
    ].filter(Boolean).join(' ');

    const initialColumnVisibility = useMemo(() => (
        initialHiddenColumns.reduce((visibility, fieldName) => {
            visibility[fieldName] = false;
            return visibility;
        }, {})
    ), [initialHiddenColumns]);

    const tableElement = tableColumns.length > 0 ? (
        <MaterialReactTable
            columns={tableColumns}
            data={data}
            enablePinning={!useFrozenRightPanel && (hasCustomActionColumn || Boolean(left_pinned?.length) || Boolean(right_pinned?.length))}
            enableStickyHeader
            enableColumnResizing
            columnResizeMode="onEnd"
            enableColumnActions={false}
            enableColumnFilters={enableTableSearch}
            enableGlobalFilter={enableTableSearch}
            enableFilters={enableTableSearch}
            enableHiding={enableTableSearch || enableExportToolbar}
            enableFullScreenToggle={enableTableSearch || enableExportToolbar}
            enablePagination={true}
            enableSorting={true}
            enableBottomToolbar={true}
            enableTopToolbar={enableTableSearch || enableExportToolbar}
            enableToolbarInternalActions={enableTableSearch || enableExportToolbar}
            tableInstanceRef={tableInstanceRef}
            positionGlobalFilter="right"
            muiSearchTextFieldProps={{
                placeholder: 'Search...',
                size: 'small',
                variant: 'outlined',
                sx: { minWidth: '220px', backgroundColor: '#fff' },
            }}
            muiTableBodyRowProps={{
                hover: true,
                sx: isCompactTable ? {
                    height: 'auto',
                } : undefined,
            }}
            enableDensityToggle={enableTableSearch || enableExportToolbar}
            state={{ isLoading, pagination, columnPinning: columnPinningState }}
            onPaginationChange={(updater) => {
                setPagination((prev) => (
                    typeof updater === 'function' ? updater(prev) : updater
                ));
            }}
            muiTablePaginationProps={{
                rowsPerPageOptions: [5, 10, 15, 20, 25, 50, 100],
                SelectProps: {
                    MenuProps: {
                        disablePortal: true,
                        sx: { zIndex: 3000 },
                    },
                },
            }}
            muiTablePaperProps={{
                elevation: 0,
                sx: {
                    borderRadius: '0',
                    border: '1px solid #000000',
                    borderRight: useFrozenRightPanel ? 'none' : '1px solid #000000',
                    overflow: 'visible',
                },
            }}
            muiTableBodyProps={{
                sx: () => ({
                    '& tr:nth-of-type(odd)': {
                        backgroundColor: '#bcbfc4',
                    },
                }),
            }}
            muiTableHeadCellProps={{
                align: 'center',
                sx: {
                    fontWeight: 700,
                    fontSize: isCompactTable ? '12px' : '14px',
                    letterSpacing: '0.02em',
                    background: 'linear-gradient(135deg, #186098 0%, #0090D8 52%, #2A88C0 100%)',
                    backgroundColor: '#2A88C0',
                    color: '#ffffff',
                    textAlign: 'center',
                    borderBottom: '2px solid #186098',
                    borderRight: '1px solid rgba(255, 255, 255, 0.18)',
                    py: isCompactTable ? '6px' : '8px',
                    px: isCompactTable ? '8px' : '10px',
                    whiteSpace: 'nowrap',
                    lineHeight: 1.25,
                    '& .MuiTableSortLabel-root': {
                        color: '#ffffff',
                        justifyContent: 'center',
                        width: '100%',
                    },
                    '& .MuiTableSortLabel-icon': {
                        color: 'rgba(255, 255, 255, 0.9) !important',
                    },
                    '& .MuiIconButton-root': {
                        color: '#ffffff',
                    },
                },
            }}
            muiTableBodyCellProps={isCompactTable ? {
                sx: {
                    fontSize: '12px',
                    lineHeight: 1.2,
                    py: '2px',
                    px: '6px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                },
            } : undefined}
            initialState={{
                density: 'compact',
                pagination: { pageIndex: 0, pageSize: defaultPageSize },
                columnPinning: columnPinningState,
                columnVisibility: initialColumnVisibility,
                sorting: initialSorting,
            }}
            muiTableContainerProps={{
                sx: {
                    overflow: 'auto',
                    maxWidth: '100%',
                    width: layoutFullWidth ? '100%' : undefined,
                },
            }}
            muiTopToolbarProps={{
                sx: {
                    overflow: 'visible',
                    flexWrap: 'wrap',
                    gap: '4px',
                    minHeight: '36px',
                    py: '2px',
                    px: '8px',
                    '& .MuiIconButton-root': {
                        padding: '4px',
                    },
                },
            }}
            muiTableProps={layoutFullWidth ? {
                sx: {
                    width: useFrozenRightPanel ? 'max-content' : '100%',
                    minWidth: useFrozenRightPanel ? '100%' : undefined,
                },
            } : undefined}
            defaultColumn={layoutFullWidth ? {
                minSize: useFrozenRightPanel ? 72 : 80,
                maxSize: 800,
            } : undefined}
            renderTopToolbarCustomActions={() => {
                if (enableExportToolbar && (onCSVExport || onPDFExport)) {
                    return (
                        <ReportExportDropdown
                            onExcel={onCSVExport}
                            onPDF={onPDFExport}
                        />
                    );
                }
                return null;
            }}
        />
    ) : null;

    return (
        <div className={wrapperClassName} style={{ paddingTop: '2px', paddingBottom: '2px' }}>
            <h1 style={{ fontSize: '22px', color: 'darkblue', fontWeight: 'bold', display: 'none' }}>{title}</h1>
            {useFrozenRightPanel ? (
                <div className="mrt-split-freeze-layout">
                    <div
                        className="mrt-split-freeze-main"
                        style={{ paddingRight: `${frozenPanelWidth}px` }}
                    >
                        {tableElement}
                    </div>
                    <div
                        className="mrt-split-freeze-panel"
                        style={{ width: `${frozenPanelWidth}px` }}
                    >
                        <div className="mrt-split-freeze-panel-toolbar" />
                        <div className="mrt-split-freeze-panel-header">
                            {frozenRightColumnDefs.map((col) => (
                                <div
                                    key={col.fieldName}
                                    className={`mrt-split-freeze-panel-col${col.headerName === 'ROW_ACTIONS' ? ' is-action' : ''}`}
                                    style={{ flex: `0 0 ${getFrozenColumnWidth(col)}px`, width: `${getFrozenColumnWidth(col)}px` }}
                                >
                                    {col.headerName === 'ROW_ACTIONS' ? 'Action' : col.headerName}
                                </div>
                            ))}
                        </div>
                        <div className="mrt-split-freeze-panel-body">
                            {isLoading ? (
                                <div className="mrt-split-freeze-panel-loading">...</div>
                            ) : (
                                visibleRows.map((row, index) => (
                                    <div
                                        key={row.id}
                                        className={`mrt-split-freeze-panel-row${index % 2 === 1 ? ' is-alt' : ''}`}
                                    >
                                        {frozenRightColumnDefs.map((col) => (
                                            <div
                                                key={col.fieldName}
                                                className={`mrt-split-freeze-panel-col${col.headerName === 'ROW_ACTIONS' ? ' is-action' : ''}`}
                                                style={{ flex: `0 0 ${getFrozenColumnWidth(col)}px`, width: `${getFrozenColumnWidth(col)}px` }}
                                            >
                                                {renderFrozenCell(col, row.original)}
                                            </div>
                                        ))}
                                    </div>
                                ))
                            )}
                        </div>
                        <div className="mrt-split-freeze-panel-footer" />
                    </div>
                </div>
            ) : (
                tableElement
            )}
        </div>
    );
}

export default ReactTableWrapper;
